import { useQuery } from '@apollo/client';
import { useDebounce } from 'use-debounce';

import React, { FC, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { GET_RANDOM_ARTICLES, GET_SEARCH } from 'Queries';
import { Promos } from 'Components/Promos';

import './Search.scss';

export const Search: FC = () => {
  const { push, query: _query } = useRouter();

  const [query, setQuery] = useState(_query?.search ?? '');
  const [slowQuery] = useDebounce(query, 1000, { trailing: true });

  const isValid = (query?.length ?? 0) > 2;
  const isDebouncing = query != slowQuery;

  const options = useMemo(() => ({ variables: { search: slowQuery } }), [slowQuery]);

  const { data: articleData, loading: isLoading } = useQuery(GET_SEARCH, options);
  const { data: randomArticleData } = useQuery(GET_RANDOM_ARTICLES);
  const isUnknown = isDebouncing || isLoading;

  const articles = articleData?.articles ?? [];
  const randomArticles = randomArticleData?.randomArticles ?? [];

  useEffect(() => {
    push(`/search/${slowQuery}/`, undefined, { shallow: true });
  }, [slowQuery]);

  return (
    <div data-page="search" data-visible={true}>
      <Head>
        <title>{`${query}`} | The Platform UK</title>

        <meta property="og:site_name" content="The Platform" />
        <meta name="twitter:site" content="@YourPlatformUK" />
      </Head>

      <div className={`section`}>
        <div className="container">
          <div className="section-label">Search</div>

          <div>
            <input
              type="search"
              autoFocus={true}
              className="search-input"
              defaultValue={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={'Search across our articles'}
            />
          </div>
        </div>
      </div>

      <div className="section section-alt search-results">
        <div className="container">
          {slowQuery && (
            <div className="section-label">
              {!isUnknown && isValid && !articles.length && <>No results for&nbsp;</>}

              <span dangerouslySetInnerHTML={{ __html: query as string }} />
            </div>
          )}

          {isUnknown && <div> Finding articles for {slowQuery}...</div>}
          {isValid && !isUnknown && <Promos articles={articles} isFormatted={true} />}

          {!isUnknown && isValid && !articles.length && (
            <>
              <div>&nbsp;</div>
              <div> Check out some of our other articles</div>
              <div>&nbsp;</div>
              <Promos articles={randomArticles} isFormatted={true} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
